<script lang="ts">
  import Input from '../../../components/input/Input.svelte'
  import i18n from '../../../i18n'
  import { ChevronDown, CircleCheck } from '@lucide/svelte'
  import IconButton from '../../../components/icon-button/IconButton.svelte'
  import {
    DropdownContainer,
    DropdownTrigger,
    DropdownContent,
    DropdownItem,
  } from '../../../components/dropdown'
  import { useProjectQueries } from '../../../hooks/project'
  import ShareModal from '../components/ShareModal.svelte'
  import LastUpdate from '../components/LastUpdate.svelte'

  const { project }: { project: Project } = $props()

  let isEditable = $state(false)
  let isShareModalOpen = $state(false)
  let formElement: HTMLFormElement

  const {
    updateProjectNameMutation,
    useProjectQuery,
    useProjectSaveIsMutating,
  } = useProjectQueries()
  const isMutating = useProjectSaveIsMutating()
  const projectQuery = useProjectQuery()

  function handleSubmit(event: SubmitEvent) {
    event.preventDefault()
    const formData = new FormData(event.currentTarget as HTMLFormElement)
    const name = formData.get('name') as string

    if (!isEditable) isEditable = true
    else if (name === project.name) isEditable = false
    else updateProjectNameMutation.mutate(name)
  }

  function onRenameClick() {
    isEditable = true
    setTimeout(() => {
      const input = formElement.elements.namedItem('name') as HTMLInputElement

      input.focus()
      input.select()
    }, 100)
  }

  $effect(() => {
    if (updateProjectNameMutation.isSuccess) {
      isEditable = false
      updateProjectNameMutation.reset()
    }
  })
</script>

<DropdownContainer>
  <DropdownTrigger class="project-name-user-infos">
    <div class="container">
      <form bind:this={formElement} class="form" onsubmit={handleSubmit}>
        {#if isEditable}
          <Input
            name="name"
            autocomplete="off"
            autofocus
            defaultValue={project.name}
            disabled={updateProjectNameMutation.isPending}
            label={$i18n.t('project.name')}
            onclick={updateProjectNameMutation.reset}
            readonly={updateProjectNameMutation.isPending}
            variant="ghost"
          />
          <IconButton iconSize={1} size={1.5} type="submit">
            <CircleCheck size={16} />
          </IconButton>
        {:else}
          <div class="project-name">
            {project.name}
          </div>
          <IconButton iconSize={1} size={1.5} type="button">
            <ChevronDown size={16} />
          </IconButton>
        {/if}
      </form>
      <LastUpdate
        isLoading={isMutating.current > 0}
        updatedAt={projectQuery.data?.data.updatedAt}
      />
    </div>
  </DropdownTrigger>
  <DropdownContent placement="right-end">
    <DropdownItem onclick={onRenameClick}>
      {$i18n.t('navbar.dropdown.renameProject')}
    </DropdownItem>
    <DropdownItem onclick={() => (isShareModalOpen = true)}>
      {$i18n.t('navbar.dropdown.shareProject')}
    </DropdownItem>
    <DropdownItem href="/projects">
      {$i18n.t('navbar.dropdown.backToProjects')}
    </DropdownItem>
  </DropdownContent>
</DropdownContainer>

<ShareModal bind:open={isShareModalOpen} />

<style lang="scss">
  .container {
    padding: 0 1rem;
    min-height: 4rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: start;

    .form {
      display: flex;
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
      color: var(--muted-foreground);

      :global(input) {
        max-height: 1.5rem;
        border-radius: 0;
      }

      .project-name {
        color: var(--foreground);
        flex: 1;
        text-align: start;
      }
    }
  }
</style>
